<template>
	<v-container>
		<v-skeleton-loader
			v-if="loading"
			type="card"
		/>
		<v-row v-else-if="order">
			<v-col cols="12">
				<v-card flat>
					<v-card-title class="pb-0">
						Заявка на ремонт #{{ order.id }}
						<v-chip
							v-for="tag in order.tags"
							:key="tag.id"
							:color="tag.color"
							class="ml-1"
							outlined
							small
							close
							@click:close="removeTag(tag)"
						>
							{{ tag.display_name }}
						</v-chip>
						<repair-orders-tag-create
							:order-id="order.id"
							@created="pushTag"
						/>
					</v-card-title>

					<v-row v-if="!$vuetify.breakpoint.xsOnly">
						<v-col
							v-for="(tabData, key) in tabsData"
							:key="key"
							cols="12"
							md="6"
							lg="4"
						>
							<v-card outlined>
								<component
									v-if="tabData.hasOwnProperty('component')"
									:is="tabData.component.name"
									v-bind="tabData.component.props"
									v-on="tabData.component.events"
								/>
								<s-tabs
									v-else-if="tabData.hasOwnProperty('tabs')"
									:tabs="tabData.tabs"
								/>
							</v-card>
						</v-col>
					</v-row>
					<s-tabs
						v-else
						:tabs="mobileTabsData"
					/>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import CardRow from '@/components/base/card-row';
import STabs from '@/components/orders/repair/show/s-tabs';
import RepairOrdersTagCreate from '@/components/orders/repair/show/create-tag';

export default {
	name: 'RepairOrderShow',
	components: {
		RepairOrdersTagCreate,
		STabs,
		CardRow,
	},
	data: () => ({
		loading: true,
		order: null,
		topTab: 0,
		worksPartsTab: 0,
		allowedStates: [],
	}),
	computed: {
		tabsData() {
			return [
				{
					tabs: [
						{
							title: 'Контрагент',
							icon: 'mdi-account-details',
							component: {
								name: 'RepairOrderShowClientData',
								props: {
									order: this.order,
								},
								events: {
									'set:client-description': this.updateClientDescription,
								},
							},
						},
						{
							title: 'Диагностика',
							icon: 'mdi-card-search',
							component: {
								name: 'RepairOrderShowDiagnostics',
								props: {
									order: this.order,
								},
								events: {
									'set:breakage-cause': this.updateBreakageCause,
									'set:identified-breakages': this.updateIdentifiedBreakages,
								},
							},
						},
						{
							title: 'Сообщения',
							icon: 'mdi-forum',
							component: {
								name: 'RepairOrderShowMessages',
								props: {
									orderId: this.order.id,
								},
								events: {},
							},
						},
					],
				},
				{
					tabs: [
						{
							title: 'Позиции',
							icon: 'mdi-hammer-wrench',
							component: {
								name: 'RepairOrderPositions',
								props: {
									order: this.order,
									positions: this.order.positions,
								},
								events: {
									'created': this.pushPosition,
									'deleted': this.removePosition,
								},
							},
						},
					],
				},
				{
					tabs: [
						{
							title: 'Сводка',
							icon: 'mdi-view-list',
							component: {
								name: 'RepairOrderShowSummary',
								props: {
									order: this.order,
									allowedStates: this.allowedStates,
								},
								events: {
									'set:state': this.setState,
									'set:mechanic': this.setMechanic,
									'created:media': this.pushMedia,
									'deleted:media': this.removeMedia,
								},
							},
						},
						{
							title: 'Активность',
							icon: 'mdi-format-list-checkbox',
							component: {
								name: 'RepairOrderShowActivity',
								props: {
									order: this.order,
								},
								events: {},
							},
						}
					],
				},
			];
		},
		mobileTabsData() {
			return this.tabsData
				.map(item => item.tabs)
				.flat();
		},
	},
	async mounted() {
		await this.fetchOrder();
	},
	methods: {
		async fetchOrder() {
			this.loading = true;
			try {
				const response = await this.$http.get(`/api/v2/service/orders/repair/${this.$route.params.order}`);
				this.setOrderFromResponse(response);
			} catch (e) {
				this.$notify.error(e.message);
				await this.$router.push('/orders/repair');
			} finally {
				this.loading = false;
			}
		},
		setOrderFromResponse(response) {
			this.order = response.data.data;
			this.allowedStates = response.data.meta.allowed_states;
			this.breakage_cause = this.order.breakage_cause;
		},
		pushPosition(position) {
			this.order.positions.push(position);
			const total = position.price * position.amount;
			this.order.summary.price.total += total;
			if (position.type === 'work') {
				this.order.summary.price.works += total;
				this.order.summary.hours += position.amount;
			}
			if (position.type === 'part') {
				this.order.summary.price.parts += total;
			}
		},
		removePosition(deletedPosition) {
			this.order.positions = this.order.positions.filter(position => position.id !== deletedPosition.id);
			const total = deletedPosition.price * deletedPosition.amount;
			this.order.summary.price.total -= total;
			if (deletedPosition.type === 'work') {
				this.order.summary.price.works -= total;
				this.order.summary.hours -= deletedPosition.amount;
			}
			if (deletedPosition.type === 'part') {
				this.order.summary.price.parts -= total;
			}
		},
		pushMedia(media) {
			this.order.media.push(media);
		},
		removeMedia(media) {
			this.order.media = this.order.media.filter(file => file.id !== media.id);
		},
		async updateBreakageCause(newValue) {
			if (newValue === this.order.breakage_cause) return;
			try {
				const response = await this.$http.patch(`/api/v2/service/orders/repair/${this.order.id}`, {
					breakage_cause: newValue,
				});
				this.setOrderFromResponse(response);
				this.$notify('Причина неисправности изменена')
			} catch (e) {
				this.$notify.error(e.message);
			}
		},
		async updateIdentifiedBreakages(newValue) {
			if (newValue === this.order.identified_breakages) return;
			try {
				const response = await this.$http.patch(`/api/v2/service/orders/repair/${this.order.id}`, {
					identified_breakages: newValue,
				});
				this.setOrderFromResponse(response);
				this.$notify('Выявленные неисправности изменены')
			} catch (e) {
				this.$notify.error(e.message);
			}
		},
		async updateClientDescription(newValue) {
			if (newValue === this.order.client_description) return;
			try {
				const response = await this.$http.patch(`/api/v2/service/orders/repair/${this.order.id}`, {
					client_description: newValue,
				});
				this.setOrderFromResponse(response);
				this.$notify('Описание неисправности изменено')
			} catch (e) {
				this.$notify.error(e.message);
			}
		},
		async setState(state) {
			try {
				const response = await this.$http.patch(`/api/v2/service/orders/repair/${this.order.id}`, {
					state: state.name,
				});
				this.setOrderFromResponse(response);
				this.$notify('Статус изменен')
			} catch (e) {
				this.$notify.error(e.message);
			}
		},
		async setMechanic(mechanic) {
			try {
				const response = await this.$http.patch(`/api/v2/service/orders/repair/${this.order.id}`, {
					mechanic_id: mechanic.id,
				});
				this.setOrderFromResponse(response);
				this.$notify('Механик назначен')
			} catch (e) {
				this.$notify.error(e.message);
			}
		},
		pushTag(newTags) {
			this.order.tags = newTags;
		},
		async removeTag(tag) {
			try {
				const response = await this.$http.patch(`/api/v2/service/orders/repair/${this.order.id}`, {
					tags: {
						remove: [tag.id],
					},
				});
				this.setOrderFromResponse(response);
				this.$notify('Тег удален')
			} catch (e) {
				this.$notify.error(e.message);
			}
		},

	},
};
</script>

<style scoped>

</style>
